import { useState } from 'react';
import { Search, Filter, Download, ChevronDown, Instagram, Youtube, TrendingUp, Users, Heart, MessageCircle, Eye, ExternalLink, MoreVertical, Play, X, ThumbsUp } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { motion, AnimatePresence } from 'motion/react';
import { Dialog, DialogContent } from './ui/dialog';
import { AmbassadorHubStatistics } from './AmbassadorHubStatistics';

interface Mention {
  id: string;
  postUrl: string;
  thumbnail: string;
  caption: string;
  platform: 'Instagram' | 'YouTube' | 'TikTok';
  likes: number;
  comments: {
    user: string;
    text: string;
    timestamp: string;
  }[];
  views?: number;
  type: 'image' | 'video';
  timestamp: string;
}

interface Ambassador {
  id: string;
  handle: string;
  name: string;
  channel: 'Instagram' | 'YouTube' | 'TikTok';
  followers: number;
  engagementRate: number;
  latestBrandPost: number; // days ago
  avatar: string;
  verified: boolean;
  totalPosts: number;
  averageViews: number;
  mentions: Mention[];
  // Additional profile data
  bio?: string;
  location?: string;
  email?: string;
  interests?: string[];
  tags?: string[];
}

// Ambassador Profile Sidebar Component (50% width)
function AmbassadorProfileSidebar({ 
  ambassador, 
  isOpen, 
  onClose 
}: { 
  ambassador: Ambassador | null; 
  isOpen: boolean; 
  onClose: () => void;
}) {
  if (!ambassador) return null;

  // Mock audience data
  const audienceGender = { male: 35, female: 65 };
  const audienceAge = [
    { range: '13-17', percentage: 15 },
    { range: '18-24', percentage: 45 },
    { range: '25-34', percentage: 30 },
    { range: '35-44', percentage: 10 }
  ];
  const audienceLocation = [
    { country: 'United States', percentage: 45 },
    { country: 'United Kingdom', percentage: 20 },
    { country: 'Canada', percentage: 15 },
    { country: 'Australia', percentage: 10 },
    { country: 'Other', percentage: 10 }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 z-[100]"
            onClick={onClose}
          />

          {/* Sidebar - 50% width */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed right-0 top-0 bottom-0 w-[50%] bg-white z-[101] overflow-y-auto"
          >
            {/* Close Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="absolute top-4 right-4 z-10 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full"
            >
              <X className="w-5 h-5" />
            </Button>

            {/* Header Section */}
            <div className="px-12 py-8">
              <div className="flex items-center gap-8 mb-8">
                {/* Large Circular Avatar */}
                <img
                  src={ambassador.avatar}
                  alt={ambassador.name}
                  className="w-48 h-48 rounded-full object-cover flex-shrink-0"
                />
                
                {/* Name */}
                <div className="flex-1">
                  <h1 className="text-5xl text-gray-900 mb-2 uppercase tracking-tight">
                    {ambassador.name}
                  </h1>
                </div>
              </div>

              {/* Categories Section */}
              {((ambassador.interests && ambassador.interests.length > 0) || (ambassador.tags && ambassador.tags.length > 0)) && (
                <Card className="bg-[#f8f9fa] border-0 mb-8">
                  <CardContent className="p-6">
                    <h2 className="text-gray-900 mb-4">Categories</h2>
                    <div className="flex flex-wrap gap-2">
                      {ambassador.interests?.map((interest, idx) => (
                        <Badge 
                          key={idx} 
                          className="bg-white text-gray-700 border border-gray-200 px-4 py-2 text-sm hover:bg-gray-50"
                        >
                          {interest}
                        </Badge>
                      ))}
                      {ambassador.tags?.map((tag, idx) => (
                        <Badge 
                          key={`tag-${idx}`} 
                          className="bg-white text-gray-700 border border-gray-200 px-4 py-2 text-sm hover:bg-gray-50"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Who is Section */}
            <div className="bg-[#f8f9fa] px-12 py-8">
              <h2 className="text-gray-900 mb-4">Who is {ambassador.name}</h2>
              <p className="text-gray-700 mb-2">{ambassador.bio || 'No bio available'}</p>
              {ambassador.email && (
                <p className="text-gray-700">{ambassador.email}</p>
              )}
            </div>

            {/* Linked Platforms */}
            <div className="bg-white px-12 py-8">
              <h2 className="text-gray-900 mb-4">Linked platforms</h2>
              <div className="space-y-3">
                {ambassador.channel === 'Instagram' && (
                  <a 
                    href={`https://instagram.com/${ambassador.handle}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-blue-600 hover:underline"
                  >
                    <Instagram className="w-5 h-5" />
                    <span className="text-lg">{ambassador.handle}</span>
                  </a>
                )}
                {ambassador.channel === 'YouTube' && (
                  <a 
                    href={`https://youtube.com/@${ambassador.handle.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-blue-600 hover:underline"
                  >
                    <Youtube className="w-5 h-5" />
                    <span className="text-lg">{ambassador.handle}</span>
                  </a>
                )}
                {ambassador.channel === 'TikTok' && (
                  <a 
                    href={`https://tiktok.com/@${ambassador.handle.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-blue-600 hover:underline"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                    <span className="text-lg">{ambassador.handle}</span>
                  </a>
                )}
              </div>
            </div>

            {/* Metrics Section */}
            <div className="bg-white px-12 py-8">
              <h2 className="text-gray-900 mb-6">Metrics</h2>
              
              {/* Metric Grid */}
              <div className="grid grid-cols-3 gap-6 mb-8">
                {/* Followers */}
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Users className="w-7 h-7 text-gray-600" />
                  </div>
                  <p className="text-sm text-gray-500 mb-1">Followers</p>
                  <p className="text-3xl text-gray-900">{formatNumber(ambassador.followers)}</p>
                </div>

                {/* Engagement */}
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <TrendingUp className="w-7 h-7 text-gray-600" />
                  </div>
                  <p className="text-sm text-gray-500 mb-1">Engagement</p>
                  <p className="text-3xl text-gray-900">{ambassador.engagementRate}%</p>
                </div>

                {/* Avg views/post */}
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Eye className="w-7 h-7 text-gray-600" />
                  </div>
                  <p className="text-sm text-gray-500 mb-1">Avg. views/post</p>
                  <p className="text-3xl text-gray-900">{formatNumber(ambassador.averageViews)}</p>
                </div>
              </div>

              {/* Audience Insights */}
              <div className="mt-12">
                <h2 className="text-gray-900 mb-6">Audience insights</h2>

                {/* Gender Split */}
                <div className="mb-8">
                  <h3 className="text-gray-900 mb-6">Gender split</h3>
                  
                  {/* Donut Chart */}
                  <div className="flex justify-center mb-6">
                    <svg width="200" height="200" viewBox="0 0 200 200">
                      <circle
                        cx="100"
                        cy="100"
                        r="70"
                        fill="none"
                        stroke="#f472b6"
                        strokeWidth="40"
                        strokeDasharray={`${audienceGender.female * 4.4} 440`}
                        transform="rotate(-90 100 100)"
                      />
                      <circle
                        cx="100"
                        cy="100"
                        r="70"
                        fill="none"
                        stroke="#fbbf24"
                        strokeWidth="40"
                        strokeDasharray={`${audienceGender.male * 4.4} 440`}
                        strokeDashoffset={`-${audienceGender.female * 4.4}`}
                        transform="rotate(-90 100 100)"
                      />
                    </svg>
                  </div>

                  {/* Legend */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-yellow-400 rounded-sm"></div>
                        <span className="text-gray-700">Male</span>
                      </div>
                      <span className="text-gray-900">{audienceGender.male}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-pink-400 rounded-sm"></div>
                        <span className="text-gray-700">Female</span>
                      </div>
                      <span className="text-gray-900">{audienceGender.female}%</span>
                    </div>
                  </div>
                </div>

                {/* Age Division */}
                <div className="mb-8">
                  <h3 className="text-gray-900 mb-6">Age division</h3>
                  
                  {/* Bar Chart */}
                  <div className="flex items-end justify-around h-48 mb-6">
                    {audienceAge.map((age, idx) => (
                      <div key={idx} className="flex flex-col items-center gap-2">
                        <div 
                          className="w-12 bg-pink-400 rounded-t"
                          style={{ height: `${age.percentage * 2}px` }}
                        ></div>
                        <span className="text-xs text-gray-600">{age.range}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Top Countries */}
                <div>
                  <h3 className="text-gray-900 mb-4">Top countries</h3>
                  <div className="space-y-3">
                    {audienceLocation.map((location, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-700">{location.country}</span>
                        <span className="text-gray-900">{location.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// Mention Detail Modal Component
function MentionDetailModal({
  mention,
  isOpen,
  onClose
}: {
  mention: Mention | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  if (!mention) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="space-y-6">
          {/* Post Content */}
          <div className="relative">
            <div className="relative w-full aspect-square bg-gray-100 rounded-lg overflow-hidden">
              <img
                src={mention.thumbnail}
                alt={mention.caption}
                className="w-full h-full object-cover"
              />
              {mention.type === 'video' && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 bg-black/50 rounded-full flex items-center justify-center">
                    <Play className="w-8 h-8 text-white ml-1" />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Post Info */}
          <div>
            <div className="flex items-center gap-4 mb-4">
              <Badge className={getChannelColor(mention.platform)} variant="secondary">
                {mention.platform}
              </Badge>
              <span className="text-sm text-gray-500">{mention.timestamp}</span>
            </div>
            
            <p className="text-gray-900 mb-4">{mention.caption}</p>

            {/* Stats */}
            <div className="flex items-center gap-6 mb-6 pb-6 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <ThumbsUp className="w-5 h-5 text-gray-400" />
                <span className="text-gray-900">{formatNumber(mention.likes)} likes</span>
              </div>
              {mention.views && (
                <div className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-900">{formatNumber(mention.views)} views</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-gray-400" />
                <span className="text-gray-900">{mention.comments.length} comments</span>
              </div>
            </div>

            {/* Comments */}
            <div>
              <h3 className="text-gray-900 mb-4">Comments</h3>
              <div className="space-y-4">
                {mention.comments.map((comment, idx) => (
                  <div key={idx} className="flex gap-3">
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex-shrink-0"></div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm text-gray-900">{comment.user}</span>
                        <span className="text-xs text-gray-500">{comment.timestamp}</span>
                      </div>
                      <p className="text-sm text-gray-700">{comment.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* External Link */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <a
                href={mention.postUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-blue-600 hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                View original post
              </a>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Latest Mentions Modal Component
function LatestMentionsModal({
  mentions,
  ambassadorName,
  isOpen,
  onClose,
  onMentionClick
}: {
  mentions: Mention[];
  ambassadorName: string;
  isOpen: boolean;
  onClose: () => void;
  onMentionClick: (mention: Mention) => void;
}) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl text-gray-900 mb-2">Latest Mentions</h2>
            <p className="text-gray-600">Brand mentions by {ambassadorName}</p>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {mentions.map((mention) => (
              <Card 
                key={mention.id} 
                className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => onMentionClick(mention)}
              >
                <CardContent className="p-0">
                  {/* Thumbnail */}
                  <div className="relative w-full aspect-square bg-gray-100 overflow-hidden">
                    <img
                      src={mention.thumbnail}
                      alt={mention.caption}
                      className="w-full h-full object-cover"
                    />
                    {mention.type === 'video' && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-12 h-12 bg-black/50 rounded-full flex items-center justify-center">
                          <Play className="w-6 h-6 text-white ml-1" />
                        </div>
                      </div>
                    )}
                    
                    {/* Platform Badge */}
                    <div className="absolute top-3 right-3">
                      <Badge className={`${getChannelColor(mention.platform)} border-0`}>
                        {mention.platform}
                      </Badge>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <p className="text-sm text-gray-900 mb-3 line-clamp-2">{mention.caption}</p>
                    
                    {/* Stats */}
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <ThumbsUp className="w-3 h-3" />
                        {formatNumber(mention.likes)}
                      </div>
                      {mention.views && (
                        <div className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {formatNumber(mention.views)}
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <MessageCircle className="w-3 h-3" />
                        {mention.comments.length}
                      </div>
                      <span className="ml-auto">{mention.timestamp}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {mentions.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No brand mentions yet
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Helper functions moved outside component to be reusable
const formatNumber = (num: number) => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(0)}K`;
  }
  return num.toString();
};

const getChannelColor = (channel: string) => {
  switch (channel) {
    case 'Instagram':
      return 'bg-pink-100 text-pink-700';
    case 'YouTube':
      return 'bg-red-100 text-red-700';
    case 'TikTok':
      return 'bg-[#f3ecff] text-[#be43ff]';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

export function AmbassadorsPage() {
  const [activeTab, setActiveTab] = useState<'creators' | 'mentions' | 'statistics'>('statistics');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState<string>('followers');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedChannel, setSelectedChannel] = useState<string>('all');
  const [selectedMention, setSelectedMention] = useState<Mention | null>(null);
  const [mentionDetailOpen, setMentionDetailOpen] = useState(false);
  const [latestMentionsOpen, setLatestMentionsOpen] = useState(false);
  const [selectedAmbassadorMentions, setSelectedAmbassadorMentions] = useState<Mention[]>([]);
  const [selectedAmbassadorName, setSelectedAmbassadorName] = useState('');
  const [selectedProfile, setSelectedProfile] = useState<Ambassador | null>(null);
  const [profileSidebarOpen, setProfileSidebarOpen] = useState(false);

  // Mock ambassadors data
  const ambassadors: Ambassador[] = [
    {
      id: '1',
      handle: '@sarahfitness',
      name: 'Sarah Johnson',
      channel: 'Instagram',
      followers: 487000,
      engagementRate: 6.8,
      latestBrandPost: 2,
      avatar: 'https://i.pravatar.cc/150?img=1',
      verified: true,
      totalPosts: 24,
      averageViews: 45000,
      bio: 'Fitness enthusiast and wellness advocate. Inspiring healthy lifestyles through outdoor adventures and mindful living.',
      location: 'Los Angeles, CA',
      email: 'sarah@example.com',
      interests: ['Fitness', 'Wellness', 'Outdoor Activities'],
      tags: ['Health', 'Lifestyle', 'Adventure'],
      mentions: [
        {
          id: 'm1',
          postUrl: 'https://instagram.com/p/example1',
          thumbnail: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400&h=400&fit=crop',
          caption: 'Just tried the new energy drink from @yourbrand! Perfect for my morning workouts 💪 #sponsored',
          platform: 'Instagram',
          likes: 12400,
          views: 45000,
          type: 'image',
          timestamp: '2 days ago',
          comments: [
            { user: '@fitfam101', text: 'This looks amazing! Where can I get it?', timestamp: '2 days ago' },
            { user: '@healthylife', text: 'Been looking for something like this!', timestamp: '2 days ago' },
            { user: '@gymrat', text: 'Love your content Sarah! 🔥', timestamp: '1 day ago' }
          ]
        },
        {
          id: 'm2',
          postUrl: 'https://instagram.com/p/example2',
          thumbnail: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=400&h=400&fit=crop',
          caption: 'Recovery day essentials with @yourbrand protein shake 🥤',
          platform: 'Instagram',
          likes: 9800,
          views: 38000,
          type: 'video',
          timestamp: '5 days ago',
          comments: [
            { user: '@proteinfan', text: 'What flavor is that?', timestamp: '5 days ago' },
            { user: '@fitgoals', text: 'Need to try this!', timestamp: '4 days ago' }
          ]
        }
      ]
    },
    {
      id: '2',
      handle: '@mikewellness',
      name: 'Mike Chen',
      channel: 'YouTube',
      followers: 1250000,
      engagementRate: 4.2,
      latestBrandPost: 5,
      avatar: 'https://i.pravatar.cc/150?img=2',
      verified: true,
      totalPosts: 18,
      averageViews: 125000,
      bio: 'Wellness coach and nutrition expert sharing science-backed health tips.',
      location: 'San Francisco, CA',
      interests: ['Nutrition', 'Wellness', 'Science'],
      tags: ['Health Coach', 'Education'],
      mentions: [
        {
          id: 'm3',
          postUrl: 'https://youtube.com/watch?v=example',
          thumbnail: 'https://images.unsplash.com/photo-1556911220-bff31c812dba?w=400&h=400&fit=crop',
          caption: 'Full review of @yourbrand supplements - my honest thoughts after 30 days',
          platform: 'YouTube',
          likes: 8500,
          views: 125000,
          type: 'video',
          timestamp: '5 days ago',
          comments: [
            { user: 'HealthSeeker123', text: 'Thanks for the honest review!', timestamp: '5 days ago' },
            { user: 'WellnessJourney', text: 'This is exactly what I needed to see', timestamp: '4 days ago' }
          ]
        }
      ]
    },
    {
      id: '3',
      handle: '@outdoorjenna',
      name: 'Jenna Martinez',
      channel: 'Instagram',
      followers: 312000,
      engagementRate: 8.5,
      latestBrandPost: 1,
      avatar: 'https://i.pravatar.cc/150?img=3',
      verified: true,
      totalPosts: 32,
      averageViews: 38000,
      bio: 'Adventure photographer capturing the beauty of the great outdoors.',
      location: 'Denver, CO',
      interests: ['Photography', 'Hiking', 'Travel'],
      tags: ['Adventure', 'Nature', 'Outdoor'],
      mentions: [
        {
          id: 'm4',
          postUrl: 'https://instagram.com/p/example3',
          thumbnail: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=400&h=400&fit=crop',
          caption: 'Exploring mountain trails with @yourbrand gear! Best hiking boots ever 🥾⛰️',
          platform: 'Instagram',
          likes: 15600,
          views: 42000,
          type: 'image',
          timestamp: '1 day ago',
          comments: [
            { user: '@hikerlife', text: 'Those boots look incredible!', timestamp: '1 day ago' },
            { user: '@mountainlover', text: 'Where is this trail?', timestamp: '1 day ago' },
            { user: '@adventureseeker', text: 'Adding to my wishlist!', timestamp: '20 hours ago' }
          ]
        },
        {
          id: 'm5',
          postUrl: 'https://instagram.com/p/example4',
          thumbnail: 'https://images.unsplash.com/photo-1445427865184-98cf26632e59?w=400&h=400&fit=crop',
          caption: 'Sunset hike essentials by @yourbrand 🌅',
          platform: 'Instagram',
          likes: 11200,
          views: 35000,
          type: 'video',
          timestamp: '4 days ago',
          comments: [
            { user: '@outdoorgear', text: 'Perfect timing!', timestamp: '4 days ago' }
          ]
        }
      ]
    },
    {
      id: '4',
      handle: '@trailrunner_alex',
      name: 'Alex Thompson',
      channel: 'TikTok',
      followers: 892000,
      engagementRate: 12.3,
      latestBrandPost: 3,
      avatar: 'https://i.pravatar.cc/150?img=4',
      verified: true,
      totalPosts: 45,
      averageViews: 215000,
      bio: 'Trail runner and outdoor enthusiast. Pushing limits and inspiring others to embrace the outdoors.',
      location: 'Boulder, CO',
      interests: ['Running', 'Fitness', 'Outdoor'],
      tags: ['Trail Running', 'Motivation'],
      mentions: [
        {
          id: 'm6',
          postUrl: 'https://tiktok.com/@example',
          thumbnail: 'https://images.unsplash.com/photo-1486218119243-13883505764c?w=400&h=400&fit=crop',
          caption: 'Morning run fuel powered by @yourbrand! 🏃‍♂️⚡',
          platform: 'TikTok',
          likes: 28500,
          views: 215000,
          type: 'video',
          timestamp: '3 days ago',
          comments: [
            { user: '@runner123', text: 'What a view!', timestamp: '3 days ago' },
            { user: '@fitnesslover', text: 'Incredible content as always', timestamp: '2 days ago' }
          ]
        }
      ]
    },
    {
      id: '5',
      handle: '@naturesophie',
      name: 'Sophie Williams',
      channel: 'Instagram',
      followers: 156000,
      engagementRate: 7.1,
      latestBrandPost: 7,
      avatar: 'https://i.pravatar.cc/150?img=5',
      verified: false,
      totalPosts: 21,
      averageViews: 18000,
      bio: 'Nature lover and mindful explorer. Finding peace in the wilderness.',
      location: 'Portland, OR',
      interests: ['Nature', 'Photography', 'Mindfulness'],
      tags: ['Nature Lover', 'Outdoor'],
      mentions: [
        {
          id: 'm7',
          postUrl: 'https://instagram.com/p/example5',
          thumbnail: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=400&fit=crop',
          caption: 'Forest therapy with @yourbrand essentials 🌲✨',
          platform: 'Instagram',
          likes: 7200,
          views: 18000,
          type: 'image',
          timestamp: '7 days ago',
          comments: [
            { user: '@naturelover', text: 'So peaceful!', timestamp: '7 days ago' }
          ]
        }
      ]
    },
    {
      id: '6',
      handle: '@hikingwithben',
      name: 'Ben Anderson',
      channel: 'YouTube',
      followers: 678000,
      engagementRate: 5.9,
      latestBrandPost: 4,
      avatar: 'https://i.pravatar.cc/150?img=6',
      verified: true,
      totalPosts: 15,
      averageViews: 89000,
      bio: 'Hiking guide sharing epic trails and outdoor adventures worldwide.',
      location: 'Seattle, WA',
      interests: ['Hiking', 'Adventure', 'Travel'],
      tags: ['Hiking Guide', 'Outdoor'],
      mentions: [
        {
          id: 'm8',
          postUrl: 'https://youtube.com/watch?v=example2',
          thumbnail: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=400&h=400&fit=crop',
          caption: '10 Essential Hiking Tips feat. @yourbrand Gear Review',
          platform: 'YouTube',
          likes: 6800,
          views: 89000,
          type: 'video',
          timestamp: '4 days ago',
          comments: [
            { user: 'HikerPro', text: 'Great tips Ben!', timestamp: '4 days ago' },
            { user: 'TrailSeeker', text: 'Adding this gear to my list', timestamp: '3 days ago' }
          ]
        }
      ]
    },
    {
      id: '7',
      handle: '@activemaya',
      name: 'Maya Patel',
      channel: 'TikTok',
      followers: 1450000,
      engagementRate: 10.2,
      latestBrandPost: 6,
      avatar: 'https://i.pravatar.cc/150?img=7',
      verified: true,
      totalPosts: 38,
      averageViews: 320000,
      bio: 'Fitness creator inspiring millions to live healthier, happier lives.',
      location: 'Miami, FL',
      interests: ['Fitness', 'Wellness', 'Lifestyle'],
      tags: ['Fitness Influencer', 'Wellness'],
      mentions: [
        {
          id: 'm9',
          postUrl: 'https://tiktok.com/@example2',
          thumbnail: 'https://images.unsplash.com/photo-1518310383802-640c2de311b2?w=400&h=400&fit=crop',
          caption: '30-day transformation with @yourbrand supplements! Results speak for themselves 💪',
          platform: 'TikTok',
          likes: 45200,
          views: 320000,
          type: 'video',
          timestamp: '6 days ago',
          comments: [
            { user: '@fitgoals22', text: 'This is inspiring!', timestamp: '6 days ago' },
            { user: '@healthylifestyle', text: 'Ordering now!', timestamp: '5 days ago' }
          ]
        }
      ]
    },
    {
      id: '8',
      handle: '@outdoorlife_chris',
      name: 'Chris Rodriguez',
      channel: 'Instagram',
      followers: 234000,
      engagementRate: 6.4,
      latestBrandPost: 9,
      avatar: 'https://i.pravatar.cc/150?img=8',
      verified: false,
      totalPosts: 27,
      averageViews: 22000,
      bio: 'Outdoor photographer capturing adventures one frame at a time.',
      location: 'Austin, TX',
      interests: ['Photography', 'Outdoor', 'Travel'],
      tags: ['Photographer', 'Adventure'],
      mentions: [
        {
          id: 'm10',
          postUrl: 'https://instagram.com/p/example6',
          thumbnail: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=400&fit=crop',
          caption: 'Epic sunrise captured with @yourbrand camera gear 📸',
          platform: 'Instagram',
          likes: 8900,
          views: 22000,
          type: 'image',
          timestamp: '9 days ago',
          comments: [
            { user: '@photolover', text: 'Stunning shot!', timestamp: '9 days ago' }
          ]
        }
      ]
    },
    {
      id: '9',
      handle: '@trailblazer_emma',
      name: 'Emma Davis',
      channel: 'YouTube',
      followers: 945000,
      engagementRate: 4.8,
      latestBrandPost: 11,
      avatar: 'https://i.pravatar.cc/150?img=9',
      verified: true,
      totalPosts: 19,
      averageViews: 112000,
      bio: 'Adventure vlogger exploring the world one trail at a time.',
      location: 'Denver, CO',
      interests: ['Adventure', 'Vlogging', 'Travel'],
      tags: ['Vlogger', 'Adventure'],
      mentions: [
        {
          id: 'm11',
          postUrl: 'https://youtube.com/watch?v=example3',
          thumbnail: 'https://images.unsplash.com/photo-1483728642387-6c3bdd6c93e5?w=400&h=400&fit=crop',
          caption: 'My Week with @yourbrand Adventure Pack - Full Review!',
          platform: 'YouTube',
          likes: 9200,
          views: 112000,
          type: 'video',
          timestamp: '11 days ago',
          comments: [
            { user: 'AdventureSeeker', text: 'Great review Emma!', timestamp: '11 days ago' }
          ]
        }
      ]
    },
    {
      id: '10',
      handle: '@adventuretime_jake',
      name: 'Jake Wilson',
      channel: 'TikTok',
      followers: 567000,
      engagementRate: 11.7,
      latestBrandPost: 8,
      avatar: 'https://i.pravatar.cc/150?img=10',
      verified: true,
      totalPosts: 41,
      averageViews: 178000,
      bio: 'Extreme sports enthusiast sharing adrenaline-pumping adventures.',
      location: 'San Diego, CA',
      interests: ['Extreme Sports', 'Adventure', 'Lifestyle'],
      tags: ['Extreme Sports', 'Adrenaline'],
      mentions: [
        {
          id: 'm12',
          postUrl: 'https://tiktok.com/@example3',
          thumbnail: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=400&fit=crop',
          caption: 'BASE jumping with @yourbrand action camera! 🪂',
          platform: 'TikTok',
          likes: 32400,
          views: 178000,
          type: 'video',
          timestamp: '8 days ago',
          comments: [
            { user: '@extremesports', text: 'Insane footage!', timestamp: '8 days ago' }
          ]
        }
      ]
    }
  ];

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'Instagram':
        return <Instagram className="w-4 h-4" />;
      case 'YouTube':
        return <Youtube className="w-4 h-4" />;
      case 'TikTok':
        return (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
          </svg>
        );
      default:
        return null;
    }
  };

  const getDaysText = (days: number) => {
    if (days === 1) return '1 day ago';
    if (days < 7) return `${days} days ago`;
    if (days < 14) return '1 week ago';
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    return `${Math.floor(days / 30)} months ago`;
  };

  const getEngagementColor = (rate: number) => {
    if (rate >= 10) return 'text-green-600 bg-green-50';
    if (rate >= 6) return 'text-blue-600 bg-blue-50';
    if (rate >= 3) return 'text-orange-600 bg-orange-50';
    return 'text-gray-600 bg-gray-50';
  };

  const getActivityColor = (days: number) => {
    if (days <= 3) return 'text-green-600';
    if (days <= 7) return 'text-blue-600';
    if (days <= 14) return 'text-orange-600';
    return 'text-red-600';
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const filteredAmbassadors = ambassadors
    .filter(amb => 
      (selectedChannel === 'all' || amb.channel === selectedChannel) &&
      (searchTerm === '' || 
        amb.handle.toLowerCase().includes(searchTerm.toLowerCase()) ||
        amb.name.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => {
      let aVal, bVal;
      switch (sortColumn) {
        case 'handle':
          aVal = a.handle.toLowerCase();
          bVal = b.handle.toLowerCase();
          break;
        case 'followers':
          aVal = a.followers;
          bVal = b.followers;
          break;
        case 'engagementRate':
          aVal = a.engagementRate;
          bVal = b.engagementRate;
          break;
        case 'latestBrandPost':
          aVal = a.latestBrandPost;
          bVal = b.latestBrandPost;
          break;
        case 'mentions':
          aVal = a.mentions.length;
          bVal = b.mentions.length;
          break;
        default:
          return 0;
      }
      
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal as string)
          : (bVal as string).localeCompare(aVal);
      }
      
      return sortDirection === 'asc' 
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });

  const totalFollowers = ambassadors.reduce((sum, amb) => sum + amb.followers, 0);
  const averageEngagement = ambassadors.reduce((sum, amb) => sum + amb.engagementRate, 0) / ambassadors.length;
  const activeAmbassadors = ambassadors.filter(amb => amb.latestBrandPost <= 7).length;

  const handleLatestMentionsClick = (ambassador: Ambassador) => {
    setSelectedAmbassadorMentions(ambassador.mentions);
    setSelectedAmbassadorName(ambassador.name);
    setLatestMentionsOpen(true);
  };

  const handleMentionClick = (mention: Mention) => {
    setSelectedMention(mention);
    setMentionDetailOpen(true);
    setLatestMentionsOpen(false);
  };

  const handleProfileClick = (ambassador: Ambassador) => {
    setSelectedProfile(ambassador);
    setProfileSidebarOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] p-2.5">
      <div className="min-h-[calc(100vh-20px)] rounded-2xl bg-white border border-gray-200/80 shadow-sm overflow-hidden">
      <div className="max-w-[1600px] mx-auto p-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-gray-900 mb-2">Brand Ambassadors</h1>
              <p className="text-gray-600">
                Manage and track your creator partnerships
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" className="gap-2">
                <Filter className="w-4 h-4" />
                Filters
              </Button>
              <Button variant="outline" className="gap-2">
                <Download className="w-4 h-4" />
                Export
              </Button>
              <Button className="gap-2 bg-[#2e024c] hover:bg-[#3a0d5e] text-white">
                <Users className="w-4 h-4" />
                Add Ambassador
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-6 border-b border-gray-200 mb-6">
            <button
              onClick={() => setActiveTab('statistics')}
              className={`pb-3 px-1 border-b-2 transition-colors ${
                activeTab === 'statistics'
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('creators')}
              className={`pb-3 px-1 border-b-2 transition-colors ${
                activeTab === 'creators'
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Creators
            </button>
            <button
              onClick={() => setActiveTab('mentions')}
              className={`pb-3 px-1 border-b-2 transition-colors ${
                activeTab === 'mentions'
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Mentions
            </button>
          </div>
        </div>

        {/* Conditional Tab Content */}
        {activeTab === 'statistics' && <AmbassadorHubStatistics />}

        {activeTab === 'creators' && (
          <>
            {/* Stats Overview */}
            <div className="grid grid-cols-4 gap-4 mb-6">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Users className="w-5 h-5 text-[#be43ff]" />
                  <Badge className="bg-[#f3ecff] text-[#be43ff] border-0">
                    Total
                  </Badge>
                </div>
                <div className="text-2xl text-gray-900 mb-1">{ambassadors.length}</div>
                <div className="text-xs text-gray-600">Active Ambassadors</div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  <Badge className="bg-blue-100 text-blue-700 border-0">
                    Reach
                  </Badge>
                </div>
                <div className="text-2xl text-gray-900 mb-1">{formatNumber(totalFollowers)}</div>
                <div className="text-xs text-gray-600">Total Followers</div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Heart className="w-5 h-5 text-pink-600" />
                  <Badge className="bg-pink-100 text-pink-700 border-0">
                    Avg
                  </Badge>
                </div>
                <div className="text-2xl text-gray-900 mb-1">{averageEngagement.toFixed(1)}%</div>
                <div className="text-xs text-gray-600">Avg Engagement Rate</div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <MessageCircle className="w-5 h-5 text-green-600" />
                  <Badge className="bg-green-100 text-green-700 border-0">
                    Active
                  </Badge>
                </div>
                <div className="text-2xl text-gray-900 mb-1">{activeAmbassadors}</div>
                <div className="text-xs text-gray-600">Posted This Week</div>
              </CardContent>
            </Card>
          </div>

          {/* Search and Filter Bar */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search ambassadors by handle or name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#be43ff]"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSelectedChannel('all')}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                      selectedChannel === 'all'
                        ? 'bg-[#2e024c] text-white'
                        : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    All Channels
                  </button>
                  <button
                    onClick={() => setSelectedChannel('Instagram')}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                      selectedChannel === 'Instagram'
                        ? 'bg-pink-600 text-white'
                        : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Instagram className="w-4 h-4" />
                    Instagram
                  </button>
                  <button
                    onClick={() => setSelectedChannel('YouTube')}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                      selectedChannel === 'YouTube'
                        ? 'bg-red-600 text-white'
                        : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Youtube className="w-4 h-4" />
                    YouTube
                  </button>
                  <button
                    onClick={() => setSelectedChannel('TikTok')}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                      selectedChannel === 'TikTok'
                        ? 'bg-[#2e024c] text-white'
                        : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                    TikTok
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ambassadors Table */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left">
                      <button
                        onClick={() => handleSort('handle')}
                        className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        Ambassador
                        <ChevronDown className={`w-4 h-4 transition-transform ${
                          sortColumn === 'handle' && sortDirection === 'asc' ? 'rotate-180' : ''
                        }`} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left">
                      <span className="text-xs text-gray-500">Channel</span>
                    </th>
                    <th className="px-6 py-4 text-left">
                      <button
                        onClick={() => handleSort('followers')}
                        className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        Followers
                        <ChevronDown className={`w-4 h-4 transition-transform ${
                          sortColumn === 'followers' && sortDirection === 'asc' ? 'rotate-180' : ''
                        }`} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left">
                      <button
                        onClick={() => handleSort('engagementRate')}
                        className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        Engagement Rate
                        <ChevronDown className={`w-4 h-4 transition-transform ${
                          sortColumn === 'engagementRate' && sortDirection === 'asc' ? 'rotate-180' : ''
                        }`} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left">
                      <button
                        onClick={() => handleSort('mentions')}
                        className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        Mentions
                        <ChevronDown className={`w-4 h-4 transition-transform ${
                          sortColumn === 'mentions' && sortDirection === 'asc' ? 'rotate-180' : ''
                        }`} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left">
                      <button
                        onClick={() => handleSort('latestBrandPost')}
                        className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        Latest Brand Post
                        <ChevronDown className={`w-4 h-4 transition-transform ${
                          sortColumn === 'latestBrandPost' && sortDirection === 'asc' ? 'rotate-180' : ''
                        }`} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left">
                      <span className="text-xs text-gray-500">Performance</span>
                    </th>
                    <th className="px-6 py-4 text-center">
                      <span className="text-xs text-gray-500">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredAmbassadors.map((ambassador) => (
                    <tr key={ambassador.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={ambassador.avatar}
                            alt={ambassador.name}
                            className="w-10 h-10 rounded-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => handleProfileClick(ambassador)}
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-900">{ambassador.handle}</span>
                              {ambassador.verified && (
                                <svg className="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              )}
                            </div>
                            <button
                              onClick={() => handleProfileClick(ambassador)}
                              className="text-xs text-gray-500 hover:text-blue-600 transition-colors text-left"
                            >
                              {ambassador.name}
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge className={`${getChannelColor(ambassador.channel)} border-0 gap-1`}>
                          {getChannelIcon(ambassador.channel)}
                          {ambassador.channel}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{formatNumber(ambassador.followers)}</div>
                        <div className="text-xs text-gray-500">{ambassador.totalPosts} posts</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${getEngagementColor(ambassador.engagementRate)}`}>
                          <TrendingUp className="w-3 h-3" />
                          {ambassador.engagementRate}%
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleLatestMentionsClick(ambassador)}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          {ambassador.mentions.length} {ambassador.mentions.length === 1 ? 'mention' : 'mentions'}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className={`text-sm ${getActivityColor(ambassador.latestBrandPost)}`}>
                          {getDaysText(ambassador.latestBrandPost)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Eye className="w-3 h-3" />
                          {formatNumber(ambassador.averageViews)} avg views
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="gap-1"
                            onClick={() => handleProfileClick(ambassador)}
                          >
                            <ExternalLink className="w-4 h-4" />
                            View
                          </Button>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {filteredAmbassadors.length} of {ambassadors.length} ambassadors
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm">Previous</Button>
                <Button variant="outline" size="sm">Next</Button>
              </div>
            </div>
            </CardContent>
          </Card>
          </>
        )}

        {/* Mentions Tab - Coming soon placeholder */}
        {activeTab === 'mentions' && (
          <div className="text-center py-20">
            <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-gray-900 mb-2">Mentions Feed Coming Soon</h3>
            <p className="text-gray-500">Aggregated view of all brand mentions across ambassadors</p>
          </div>
        )}
      </div>

      {/* Latest Mentions Modal */}
      <LatestMentionsModal
        mentions={selectedAmbassadorMentions}
        ambassadorName={selectedAmbassadorName}
        isOpen={latestMentionsOpen}
        onClose={() => setLatestMentionsOpen(false)}
        onMentionClick={handleMentionClick}
      />

      {/* Mention Detail Modal */}
      <MentionDetailModal
        mention={selectedMention}
        isOpen={mentionDetailOpen}
        onClose={() => {
          setMentionDetailOpen(false);
          setSelectedMention(null);
        }}
      />

      {/* Profile Sidebar (50% width) */}
      <AmbassadorProfileSidebar
        ambassador={selectedProfile}
        isOpen={profileSidebarOpen}
        onClose={() => {
          setProfileSidebarOpen(false);
          setSelectedProfile(null);
        }}
      />
      </div>
    </div>
  );
}
